import {
  ButtonComponentV2,
  ExternalCardsResult,
  ExternalCardsResultTypenames,
  Typenames,
} from '@varo/graphql-schema-types';
import { useEffect, useState } from 'react';
import FetchExternalCards from 'services/graphql/queries/FetchExternalCards';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { RequestPolicy, useQuery } from 'urql';

const isExternalCardsError = (
  card: unknown,
): card is {
  __typename: 'ExternalCardsError';
  errorTitle: string;
  errorMessage: string;
} => typeof card === 'object' && card !== null && 'errorTitle' in card;

const isLinkButtonAvailable = (
  button: unknown,
): button is {
  __typename: 'ButtonComponentV2';
} =>
  typeof button === 'object' && button !== null && 'notFoundButton' in button;

const useExternalsCards = (
  requestPolicy?: RequestPolicy,
  shouldPause?: boolean,
) => {
  const [result, reExecuteQuery] = useQuery<{
    externalCards: ExternalCardsResult;
  }>({
    query: FetchExternalCards,
    requestPolicy: requestPolicy ?? 'network-only',
    pause: shouldPause ?? false,
  });
  const { data, fetching, error } = result;

  const [button, setButton] = useState<ButtonComponentV2 | undefined>();
  const responseError = useFormattedCombinedError(error);

  let errorModel = responseError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      }
    : null;

  if (
    data?.externalCards?.__typename ===
      ExternalCardsResultTypenames.ExternalCardsError &&
    isExternalCardsError(data.externalCards)
  ) {
    errorModel = {
      screenType: ErrorScreenType.Error,
      messageHeadline: data.externalCards.errorTitle,
      messageBody: data.externalCards.errorMessage,
    };
  }

  useEffect(() => {
    setButton(undefined);
    if (
      data?.externalCards?.__typename ===
        ExternalCardsResultTypenames.ExternalCardsNotFound &&
      isLinkButtonAvailable(data.externalCards)
    ) {
      // eslint-disable-next-line @typescript-eslint/dot-notation -- narrowed union type is never here; tsc requires bracket access
      setButton(data.externalCards['notFoundButton']);
    }

    if (
      data?.externalCards?.__typename ===
        ExternalCardsResultTypenames.ExternalCardsSuccess &&
      data.externalCards.button?.__typename === Typenames.ButtonComponentV2
    ) {
      setButton(data.externalCards.button);
    }
  }, [data]);

  return {
    errorModel,
    fetching,
    error,
    button,
    externalCards: data?.externalCards,
    reExecuteQuery,
  };
};

export default useExternalsCards;
